import * as Yup from 'yup';
import i18n from '@/shared/i18n';

export interface VnAddress {
  street?: string;
  ward?: string;
  province?: string;
  wardCode?: string;
  provinceCode?: string;
  country?: string;
}

export const DEFAULT_VN_COUNTRY = 'Việt Nam';

const foldCountry = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

/** Residence country is Vietnam — used to keep province/ward vs free-form foreign address. */
export const isVietnamCountry = (country?: string | null): boolean => {
  if (!country || !country.trim()) return true;
  const folded = foldCountry(country);
  return folded === 'viet nam' || folded === 'vietnam' || folded === 'vn';
};

/** Keep VN admin fields visible while the user is still typing "Việt Nam". */
export const isVietnamCountryDraft = (country?: string | null): boolean => {
  if (isVietnamCountry(country)) return true;
  const folded = foldCountry(country || '');
  if (!folded) return true;
  return 'viet nam'.startsWith(folded) || 'vietnam'.startsWith(folded);
};

export const emptyVnAddress = (): VnAddress => ({
  street: '',
  ward: '',
  province: '',
  wardCode: '',
  provinceCode: '',
  country: DEFAULT_VN_COUNTRY,
});

export const isVnAddressEmpty = (
  address: string | VnAddress | null | undefined
): boolean => {
  if (!address) return true;
  if (typeof address === 'string') return !address.trim();
  return ![address.street, address.ward, address.province].some((part) => part?.trim());
};

/** Normalize legacy free-text or partial object into a form-ready VnAddress. */
export const coerceVnAddress = (
  address: string | VnAddress | null | undefined
): VnAddress => {
  if (!address) return emptyVnAddress();
  if (typeof address === 'string') {
    return { ...emptyVnAddress(), street: address };
  }
  return {
    street: address.street || '',
    ward: address.ward || '',
    province: address.province || '',
    wardCode: address.wardCode || '',
    provinceCode: address.provinceCode || '',
    country: address.country || DEFAULT_VN_COUNTRY,
  };
};

/** Drop empty addresses before persist; keep country default when any part exists. */
export const compactVnAddress = (
  address: VnAddress | null | undefined
): VnAddress | undefined => {
  if (!address || isVnAddressEmpty(address)) return undefined;
  return {
    street: address.street?.trim() || undefined,
    ward: address.ward?.trim() || undefined,
    province: address.province?.trim() || undefined,
    wardCode: address.wardCode?.trim() || undefined,
    provinceCode: address.provinceCode?.trim() || undefined,
    country: address.country?.trim() || DEFAULT_VN_COUNTRY,
  };
};

const EMPTY_LABEL = i18n.t('validation:addressNotAvailable');

export const formatVnAddress = (
  address: string | VnAddress | null | undefined
): string => {
  if (!address) {
    return EMPTY_LABEL;
  }

  if (typeof address === 'string') {
    return address.trim() || EMPTY_LABEL;
  }

  const { street, ward, province, country } = address;
  const parts = [street, ward, province, country].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : EMPTY_LABEL;
};

export const formatVnAddressShort = (
  address: VnAddress | null | undefined
): string => {
  if (!address) {
    return EMPTY_LABEL;
  }

  const parts = [address.ward, address.province].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : EMPTY_LABEL;
};

export const vnAddressValidation = {
  street: Yup.string().required(i18n.t('validation:streetRequired')),
  ward: Yup.string().required(i18n.t('validation:wardRequired')),
  province: Yup.string().required(i18n.t('validation:provinceRequired')),
  country: Yup.string().default(DEFAULT_VN_COUNTRY),
};
